import type { Joi } from '@ksyos/koi';
import { DateTime } from '@ksyos/date-time';
import { Koi } from '@ksyos/koi';
import type { OutpatientClinicTask } from '@common/contexts/support/domain/models/outpatientClinicTaskModel';
import type { ModelAsRepositoryResource } from '@common/contexts/support/domain/types/modelType';
import type { ISchemaContainer } from './schemaContainer';
import { PatientTaskStatus } from '../enums/patientTaskStatus';
import { Profession } from '../enums/profession';
import { replaceKoiError } from './replaceKoiError';
import { timeSlotsValidator } from './timeSlots';

export type IPatientTaskAddEditValidated = ModelAsRepositoryResource<Omit<OutpatientClinicTask, 'id'>>;

export const patientTaskCustomControlDataSchema = Koi.object({
    availability: timeSlotsValidator,
});

const patientTaskEdit = Koi.object({
    customControlData: patientTaskCustomControlDataSchema,
    description: Koi.string().allow('', null).optional(),
    expectedDateCompleted: Koi.koi()
        .date()
        .custom(assertIsWeekday)
        .error((errors: Joi.ErrorReport[]) => replaceKoiError(errors, 'any.custom', 'notValidWeekend')),
    performerOrganizationId: Koi.string().uuid().allow(null),
    performerPractitionerId: Koi.string().uuid().allow(null),
    performerPractitionerProfession: Koi.koi().enum(Profession),
    typeId: Koi.string().uuid(),
}).options({ convert: false, presence: 'required' });

export const patientTaskEditWithDescription = patientTaskEdit.concat(
    Koi.object({
        description: Koi.string().required(),
    })
);

export const patientTaskEditSchema: ISchemaContainer<IPatientTaskAddEditValidated> = {
    schema: patientTaskEdit,
};

export const patientTaskEditWithDescriptionSchema: ISchemaContainer<IPatientTaskAddEditValidated> = {
    schema: patientTaskEditWithDescription,
};

const patientTaskAdd = patientTaskEdit.concat(
    Koi.object({
        episodeCategoryId: Koi.string().uuid(),
        patientId: Koi.string().uuid(),
        status: Koi.koi().enum(PatientTaskStatus),
    }).options({ convert: false, presence: 'required' })
);

export const patientTaskAddWithDescription = patientTaskAdd.concat(
    Koi.object({
        description: Koi.string().required(),
        episodeCategoryId: Koi.string().uuid(),
        patientId: Koi.string().uuid(),
        status: Koi.koi().enum(PatientTaskStatus),
    }).options({ convert: false, presence: 'required' })
);

export const patientTaskAddSchema: ISchemaContainer<IPatientTaskAddEditValidated> = {
    schema: patientTaskAdd,
};

export const patientTaskAddWithDescriptionSchema: ISchemaContainer<IPatientTaskAddEditValidated> = {
    schema: patientTaskAddWithDescription,
};

export const patientTaskAddBackendSchema: ISchemaContainer<IPatientTaskAddEditValidated> = {
    schema: patientTaskAdd.concat(
        Koi.object({
            hasDescription: Koi.boolean().optional().strip(),
        })
    ),
};

function assertIsWeekday(expectedDateCompleted: Date | string) {
    if (DateTime.from(expectedDateCompleted).isWeekend()) {
        throw new Error('Only working days are available');
    }

    return expectedDateCompleted;
}
